import { useEffect } from 'react'
import { useQueries, useQuery } from '@tanstack/react-query'
import { subscriptionService, OrderItemsParams } from '@/services/subscriptionService'
import { queryKeys } from '@/services/queryClient'
import { BillingCycleType, ItemStatus } from '@/constants'
import type { Subscription } from '@/types'
import { sortItems } from '@utils'
import { preloadImages } from '@/utils/imageCache'
import { hasFailedRebill, isLatestCycle } from '../utils/portalActions'
import { resolveProductImage } from '../utils/omp'

// Never served from cache — gcTime 0 so no stale rows paint while the refetch runs.
const ALWAYS_FRESH = { staleTime: 0, gcTime: 0 }

// Only these four end a subscription — recycle/decline/paused states stay active.
const INACTIVE_SUB_STATUSES: number[] = [
  ItemStatus.Cancelled,
  ItemStatus.Complete,
  ItemStatus.Replaced,
  ItemStatus.ThirdPartyCancel,
]

export function isActiveSubscription(item: Subscription): boolean {
  return !INACTIVE_SUB_STATUSES.includes(Number(item.status))
}

// Fetch + decode the thumbnails the cards are about to render, so the <img> paints with
// the frame that lays it out rather than whenever the browser gets round to decoding.
function usePreloadedThumbnails(items: Subscription[] | undefined) {
  const urls = (items ?? []).map(item => resolveProductImage(item)).join('\n')
  useEffect(() => {
    if (urls) {
      preloadImages(urls.split('\n'))
    }
  }, [urls])
}

export function useOrderItems(params: OrderItemsParams, enabled = true) {
  const query = useQuery({
    queryKey: queryKeys.portalOrderItems(params),
    queryFn: () => subscriptionService.getOrderItems(params),
    enabled,
    ...ALWAYS_FRESH,
  })
  usePreloadedThumbnails(query.data)
  return query
}

// Filtered client-side: the server's latest_cycle filter disagrees with billing_cycle.
export function usePaymentIssueItems(enabled: boolean) {
  const query = useOrderItems({ billing_cycle_type: BillingCycleType.Recurring }, enabled)
  const items = (query.data ?? []).filter(item => hasFailedRebill(item) && isLatestCycle(item))
  return { ...query, data: items }
}

// One card per item — no grouping by subscription_id.
export function useSubscriptions() {
  const query = useOrderItems({ billing_cycle_type: BillingCycleType.Recurring, latest_cycle: true })
  const items = sortItems(query.data ?? [])
  const active = items.filter(isActiveSubscription)
  const inactive = items.filter(item => !isActiveSubscription(item))
  return { ...query, items, active, inactive }
}

export interface OrderGroup {
  order: any
  items: Subscription[]
}

// One card per order, each holding its own item rows. No billing_cycle_type filter on
// purpose — an order mixes one-time and recurring items and the card lists all of them.
export function useOrderGroups(subscriptionId?: number | string) {
  const query = useOrderItems({
    is_active: false,
    checkout: false,
    ...(subscriptionId ? { subscription_id: subscriptionId } : {}),
  })
  const items = sortItems(query.data ?? [])
  const groups: OrderGroup[] = []
  const byOrderId = new Map<number | string, OrderGroup>()

  items.forEach(item => {
    // An item with no order stands alone rather than collapsing into a shared bucket.
    const orderId = item.order?.id ?? `item-${item.id}`
    const existing = byOrderId.get(orderId)
    if (existing) {
      existing.items.push(item)
      return
    }
    const group: OrderGroup = { order: item.order ?? {}, items: [item] }
    byOrderId.set(orderId, group)
    groups.push(group)
  })

  return { ...query, items, groups }
}

export function useCampaignProductsByIds(ids: number[] | undefined) {
  const list = ids ?? []
  return useQuery({
    queryKey: [...queryKeys.portal, 'campaign-products', list],
    queryFn: () => subscriptionService.getCampaignProductsByIds(list),
    enabled: list.length > 0,
    ...ALWAYS_FRESH,
  })
}

export function usePortalItem(itemId: string | number | undefined) {
  const query = useQuery({
    queryKey: queryKeys.portalItem(itemId!),
    queryFn: () => subscriptionService.getItem(itemId!),
    enabled: !!itemId,
    ...ALWAYS_FRESH,
  })
  usePreloadedThumbnails(query.data ? [query.data] : undefined)
  return query
}

export function useOrderTransactions(itemId?: number | string) {
  return useQuery({
    queryKey: [...queryKeys.portal, 'transactions', itemId],
    queryFn: () => subscriptionService.getTransactions(itemId!),
    enabled: itemId != null,
    ...ALWAYS_FRESH,
  })
}

export function useOrderItemsTransactions(itemIds: (number | string)[]) {
  const results = useQueries({
    queries: itemIds.map(id => ({
      queryKey: [...queryKeys.portal, 'transactions', id],
      queryFn: () => subscriptionService.getTransactions(id),
      enabled: id != null,
      ...ALWAYS_FRESH,
    })),
  })
  return results.flatMap(r => (r.data as any[]) || [])
}
